import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  DeniedPersonRefusalError,
  HttpAuthClient,
  type AuthClient,
  type DeniedPersonRefusalReason,
  type StartActingAsRequest,
} from "./auth-client";
import { accessTokenManager } from "./token-manager";
import type { AuthenticatedSession, RoleClaim } from "@/types/clm-web";

export type AuthStatus =
  | "loading"
  | "authenticated"
  | "unauthenticated"
  | "refreshing"
  | "impersonating"
  | "returning"
  | "logout";

export interface GetAccessTokenOptions {
  /** Skip the in-memory token and have the auth service mint a new one. */
  forceRefresh?: boolean;
}

export interface AuthContextValue {
  status: AuthStatus;
  session: AuthenticatedSession | undefined;
  effectiveRoleClaims: RoleClaim[];
  actualUserRoleClaims: RoleClaim[];
  /**
   * Set when the auth service refused an access-token request because the Person is denied
   * (FR-019); the session is already ended. Cleared when a signed-in session is committed.
   */
  deniedPersonRefusal: DeniedPersonRefusalReason | undefined;
  client: AuthClient;
  refreshSession: () => Promise<void>;
  getAccessToken: (options?: GetAccessTokenOptions) => Promise<string | undefined>;
  logout: () => Promise<void>;
  startActingAs: (request: StartActingAsRequest) => Promise<void>;
  stopActingAs: () => Promise<void>;
}

export interface AuthProviderProps {
  children: ReactNode;
  client?: AuthClient;
  initialSession?: AuthenticatedSession;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({
  children,
  client,
  initialSession,
}: AuthProviderProps): ReactElement {
  const clientRef = useRef<AuthClient>(client ?? new HttpAuthClient());
  const [session, setSession] = useState<AuthenticatedSession | undefined>(initialSession);
  const [status, setStatus] = useState<AuthStatus>(() =>
    statusFromSession(initialSession, "loading"),
  );
  // The subject the committed session names; the in-memory bearer was minted for it.
  const committedSubRef = useRef(initialSession?.user?.sub);
  // Bumped when the subject changes, so a token minted for the previous subject is never used.
  const tokenSubjectGenerationRef = useRef(0);
  // Bumped when a refusal ends the session, so a session read that began earlier cannot revive it.
  const refusalEpochRef = useRef(0);
  // The refusal that ended the session, until a signed-in session replaces it. The ref answers
  // later token reads synchronously (no further request); the state tells the router.
  const deniedPersonRefusalRef = useRef<DeniedPersonRefusalReason | undefined>(undefined);
  const [deniedPersonRefusal, setDeniedPersonRefusal] = useState<
    DeniedPersonRefusalReason | undefined
  >(undefined);

  const commitSession = useCallback(
    (nextSession: AuthenticatedSession, fallbackStatus: AuthStatus) => {
      committedSubRef.current = nextSession.authenticated ? nextSession.user?.sub : undefined;
      if (nextSession.authenticated) {
        deniedPersonRefusalRef.current = undefined;
        setDeniedPersonRefusal(undefined);
      }
      setSession(nextSession.authenticated ? nextSession : undefined);
      setStatus(statusFromSession(nextSession, fallbackStatus));
    },
    [],
  );

  // Same end state as an ordinary 401 refresh: no bearer, no session.
  const endSessionForDeniedPerson = useCallback(() => {
    accessTokenManager.clear();
    tokenSubjectGenerationRef.current += 1;
    refusalEpochRef.current += 1;
    commitSession(
      { authenticated: false, roleClaims: [], sessionCookieManagedByAuthService: true },
      "unauthenticated",
    );
  }, [commitSession]);

  // Set when an explicit `refreshSession()` begins. The mount-time bootstrap
  // (`getCurrentSession()`) is then stale by definition: a late-landing
  // unauthenticated bootstrap must not clear the session the refresh committed
  // (e.g. the credential-recovery return page refreshes on mount).
  const refreshStartedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const startEpoch = refusalEpochRef.current;
    const superseded = (): boolean =>
      cancelled || refreshStartedRef.current || startEpoch !== refusalEpochRef.current;

    async function loadSession(): Promise<void> {
      if (initialSession) {
        return;
      }

      setStatus("loading");
      const nextSession = await clientRef.current.getCurrentSession();

      if (!superseded()) {
        commitSession(nextSession, "authenticated");
      }
    }

    void loadSession().catch((error: unknown) => {
      if (superseded()) {
        return;
      }
      // Surface bootstrap failures instead of silently treating every user
      // as unauthenticated; an unexpected error here previously hid a
      // client-side fetch defect behind the normal logged-out experience.
      console.warn("Auth session bootstrap failed; treating user as unauthenticated.", error);
      setSession(undefined);
      setStatus("unauthenticated");
    });

    return () => {
      cancelled = true;
    };
  }, [commitSession, initialSession]);

  const refreshSession = useCallback(async () => {
    const previousSession = session;
    const epoch = refusalEpochRef.current;
    refreshStartedRef.current = true;
    setStatus("refreshing");

    try {
      const nextSession = await clientRef.current.refreshSession();
      if (epoch !== refusalEpochRef.current) {
        // A refusal ended the session while this read was in flight: its answer predates the denial.
        if (deniedPersonRefusalRef.current) {
          throw new DeniedPersonRefusalError(deniedPersonRefusalRef.current);
        }
        return;
      }
      const nextSub = nextSession.authenticated ? nextSession.user?.sub : undefined;
      if (nextSub === undefined || nextSub !== committedSubRef.current) {
        // The cached bearer names the previous subject; the next read mints one for this one.
        accessTokenManager.clear();
        tokenSubjectGenerationRef.current += 1;
      }
      commitSession(nextSession, "authenticated");
    } catch (error) {
      if (epoch !== refusalEpochRef.current) {
        // Superseded by a refusal: settle as that refusal, whatever this read's own failure was.
        if (deniedPersonRefusalRef.current) {
          throw new DeniedPersonRefusalError(deniedPersonRefusalRef.current);
        }
        throw error;
      }
      if (error instanceof DeniedPersonRefusalError) {
        // Same end state as an ordinary 401 refresh, then the caller shows the refusal.
        endSessionForDeniedPerson();
        throw error;
      }
      setStatus(statusFromSession(previousSession, "authenticated"));
      throw error;
    }
  }, [commitSession, endSessionForDeniedPerson, session]);

  const getAccessToken = useCallback(
    async (options?: GetAccessTokenOptions): Promise<string | undefined> => {
      if (deniedPersonRefusalRef.current) {
        // The session is already ended; do not ask the auth service again.
        throw new DeniedPersonRefusalError(deniedPersonRefusalRef.current);
      }

      const existingToken = accessTokenManager.getToken();
      if (!options?.forceRefresh && existingToken && !accessTokenManager.shouldRefresh()) {
        return existingToken;
      }

      const previousSession = session;
      const generation = tokenSubjectGenerationRef.current;
      setStatus("refreshing");

      try {
        const response = await clientRef.current.getAccessToken();
        if (generation !== tokenSubjectGenerationRef.current) {
          // Minted for the subject a refresh has since replaced.
          return undefined;
        }
        return accessTokenManager.setToken(response).accessToken;
      } catch (error) {
        // A refusal for a subject the session has since moved on from is not this session's.
        if (
          error instanceof DeniedPersonRefusalError &&
          generation === tokenSubjectGenerationRef.current
        ) {
          // The router brings the user to the sign-in result for this code.
          deniedPersonRefusalRef.current = error.code;
          setDeniedPersonRefusal(error.code);
          endSessionForDeniedPerson();
        }
        throw error;
      } finally {
        // Superseded (subject switch or a refusal that ended the session): the status is not ours.
        if (generation === tokenSubjectGenerationRef.current) {
          setStatus(statusFromSession(previousSession, "authenticated"));
        }
      }
    },
    [endSessionForDeniedPerson, session],
  );

  const logout = useCallback(async () => {
    setStatus("logout");
    accessTokenManager.clear();
    let redirecting = false;
    try {
      const result = await clientRef.current.logout();
      if (
        result.logoutUrl &&
        typeof window !== "undefined" &&
        window.location.href !== result.logoutUrl
      ) {
        // A full-page navigation to the identity provider is in flight; keep
        // status "logout" so ProtectedRoute shows the sign-out interstitial
        // instead of flashing the login page. The next page load re-bootstraps
        // auth state from scratch. The flag is set only after assign() returns
        // so an exception still falls through to the unauthenticated reset.
        window.location.assign(result.logoutUrl);
        redirecting = true;
      }
    } finally {
      if (!redirecting) {
        setSession(undefined);
        setStatus("unauthenticated");
      }
    }
  }, []);

  const startActingAs = useCallback(
    async (request: StartActingAsRequest) => {
      const previousSession = session;
      setStatus("impersonating");

      try {
        const nextSession = await clientRef.current.startActingAs(request);
        commitSession(nextSession, "impersonating");
      } catch (error) {
        setStatus(statusFromSession(previousSession, "authenticated"));
        throw error;
      }
    },
    [commitSession, session],
  );

  const stopActingAs = useCallback(async () => {
    const previousSession = session;
    setStatus("returning");

    try {
      const nextSession = await clientRef.current.stopActingAs();
      commitSession(nextSession, "authenticated");
    } catch (error) {
      setStatus(statusFromSession(previousSession, "authenticated"));
      throw error;
    }
  }, [commitSession, session]);

  const value = useMemo<AuthContextValue>(() => {
    const actingSession = session?.actingSession;
    const effectiveRoleClaims =
      actingSession?.status === "active"
        ? actingSession.effectiveUserRoles
        : (session?.roleClaims ?? []);
    const actualUserRoleClaims = actingSession?.actualUserRoles ?? session?.roleClaims ?? [];

    return {
      status,
      session,
      effectiveRoleClaims,
      actualUserRoleClaims,
      deniedPersonRefusal,
      client: clientRef.current,
      refreshSession,
      getAccessToken,
      logout,
      startActingAs,
      stopActingAs,
    };
  }, [
    deniedPersonRefusal,
    getAccessToken,
    logout,
    refreshSession,
    session,
    startActingAs,
    status,
    stopActingAs,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("Auth context is not available. Render within AuthProvider.");
  }

  return value;
}

function statusFromSession(
  session: AuthenticatedSession | undefined,
  authenticatedStatus: AuthStatus,
): AuthStatus {
  if (!session?.authenticated) {
    return session
      ? "unauthenticated"
      : authenticatedStatus === "loading"
        ? "loading"
        : "unauthenticated";
  }

  if (session.actingSession?.status === "active") {
    return "impersonating";
  }

  return authenticatedStatus === "impersonating" || authenticatedStatus === "returning"
    ? "authenticated"
    : authenticatedStatus;
}
